import { Alert, Button, SimpleGrid, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconChevronRight,
  IconClipboardList,
  IconTruckDelivery,
} from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import {
  ErrorState,
  Metric,
  RecordCard,
  SectionCard,
} from "../../components/ui";
import { api, type DeliveryQueueGaps } from "../../lib/api";
import { formatINR } from "../../lib/money";
import { NoteText, Page, PageNote } from "../../shell/Page";

/**
 * Delivery (requirements section 9).
 *
 * The counts are loaded rather than shown as static tiles because the first
 * question an agent has on opening this is "is there anything for me", and a
 * tile that says nothing makes them tap through to find out.
 */
export function DeliveryHome() {
  const { hasRole } = useAuth();
  const isManager = hasRole("STORE_MANAGER", "ADMIN");

  const [counts, setCounts] = useState<{ waiting: number; mine: number }>();
  const [gaps, setGaps] = useState<DeliveryQueueGaps>();
  const [error, setError] = useState<string>();
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(undefined);
      // An agent sees only their own deliveries, so the unassigned pile and
      // the gap report are not theirs to look at - and asking for them would
      // put two pointless requests on a phone on a field connection.
      if (!isManager) {
        const mine = await api.deliveryTasks("mine");
        setCounts({ waiting: 0, mine: mine.tasks.length });
        return;
      }
      const [waiting, mine, reconcile] = await Promise.all([
        api.deliveryTasks("unassigned"),
        api.deliveryTasks("mine"),
        api.deliveryGaps(),
      ]);
      setCounts({ waiting: waiting.tasks.length, mine: mine.tasks.length });
      setGaps(reconcile.gaps);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load the queue.");
    }
  }, [isManager]);

  useEffect(() => {
    void load();
  }, [load]);

  const missing = gaps ? gaps.orders.length + gaps.serviceJobs.length : 0;

  async function reconcile() {
    setSyncing(true);
    setError(undefined);
    try {
      const result = await api.reconcileDelivery();
      // Refresh BEFORE announcing. Announcing first left the "N deliveries
      // are missing" banner on screen next to a message saying they had been
      // handed over, for as long as the reload took - which reads as the
      // button not working.
      await load();
      notifications.show({
        color: "teal",
        message:
          `Handed over ${result.ordersQueued} order(s) and ` +
          `${result.serviceJobsQueued} serviced batter${
            result.serviceJobsQueued === 1 ? "y" : "ies"
          }.`,
      });
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not hand these over.",
      );
    } finally {
      setSyncing(false);
    }
  }

  return (
    <Page title="Delivery" subtitle="New goods and serviced batteries, in one queue">
      {error && <ErrorState message={error} onRetry={() => void load()} />}

      {/*
        The queue is supposed to hold everything Sales has billed and
        everything Service has finished. It is raised automatically at both of
        those moments - so if anything is missing here, something did not
        happen when it should have, and the honest thing is to say so rather
        than show an empty queue while other modules visibly have work waiting.
        That is precisely how this went wrong once: orders billed before the
        module existed had no task, and Delivery looked disconnected.
      */}
      {missing > 0 && (
        <SectionCard
          title={`${missing} ${
            missing === 1 ? "delivery is" : "deliveries are"
          } missing from this queue`}
        >
          <Stack gap="sm">
            <Alert
              variant="light"
              color="yellow"
              radius="md"
              icon={<IconAlertTriangle size={18} stroke={1.8} />}
            >
              These have been billed in Sales or finished in Service but were
              never handed over to Delivery.
            </Alert>
            <Stack gap={4}>
              {gaps?.orders.map((order) => (
                <Text key={order.id} fz="sm" c="gray.7">
                  <span className="num">{order.reference}</span> · {order.customer} ·
                  collect <span className="num">{formatINR(order.outstanding)}</span>
                </Text>
              ))}
              {gaps?.serviceJobs.map((job) => (
                <Text key={job.id} fz="sm" c="gray.7">
                  <span className="num">{job.reference}</span> · {job.customer} ·
                  battery going back
                </Text>
              ))}
            </Stack>
            {isManager ? (
              <Button loading={syncing} onClick={() => void reconcile()}>
                Hand them over now
              </Button>
            ) : (
              <Text fz="sm" c="gray.6">
                Ask the Store Manager to hand these over — it is their action.
              </Text>
            )}
          </Stack>
        </SectionCard>
      )}

      {/*
        Not a gap, and deliberately said out loud. A replacement travels on the
        order it raised, so it reaches Delivery when that order is billed - not
        when Service finishes. Without this line, a battery sitting in Service's
        "Ready for Delivery" with nothing in Delivery looks like the bug above.
      */}
      {gaps && gaps.awaitingBilling.length > 0 && (
        <SectionCard
          title={`${gaps.awaitingBilling.length} waiting on billing, not on delivery`}
        >
          <Stack gap="sm">
            {/* Said as "replacements", not "warranty replacements". Warranty
                is not judged in this app any more, and half of these are
                pro-rata rather than free. */}
            <Text fz="md" c="gray.7">
              These are service replacements. The new battery travels on its
              order, so it appears here once the Billing Executive has billed
              it.
            </Text>
            <Stack gap={4}>
              {gaps.awaitingBilling.map((job) => (
                <Text key={job.id} fz="sm" c="gray.7" className="num">
                  {job.reference} → {job.orderReference || "order not raised"}
                </Text>
              ))}
            </Stack>
          </Stack>
        </SectionCard>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        {/* "Delivery Executive Allotment" HAS MOVED to the Service module.

            The business assigns deliveries through the Service Manager, so
            the queue belongs where they work: Service -> Delivery Executive
            Allotment. It is the same screen, and the Store Manager still reaches
            it - from there rather than from here - while /delivery/queue
            redirects for anybody holding the old bookmark.

            Deliberately NOT left here as a second entry point. Two doors into
            one queue is how two people come to believe they each own it. */}

        <RecordCard
          to="/delivery/round"
          leading={<IconTruckDelivery size={21} stroke={1.6} />}
          primary="Delivery List"
          secondary={
            counts
              ? counts.mine === 0
                ? "Nothing assigned to you"
                : `${counts.mine} assigned to you`
              : "Loading…"
          }
          action={
            counts && counts.mine > 0 ? (
              <Metric value={counts.mine} size="kpi" />
            ) : (
              <Chevron />
            )
          }
        />
        {isManager && (
          <RecordCard
            to="/delivery/all"
            leading={<IconClipboardList size={21} stroke={1.6} />}
            primary="Delivery Summary"
            secondary="Every delivery still to happen, whoever has it"
            action={<Chevron />}
          />
        )}
      </SimpleGrid>

      {!isManager && counts?.mine === 0 && (
        <SectionCard>
          <Text fz="md" c="gray.7">
            Nothing is assigned to you right now. Deliveries appear here when
            the Billing Executive assigns one to you at billing, or when the
            Store Manager hands you a round.
          </Text>
        </SectionCard>
      )}

      {/* A PageNote rather than a SectionCard. The three rules still matter -
          an agent who does not know a handover needs all three proofs finds
          out at a customer's door - but as a card they were the biggest thing
          on the screen, under the tiles somebody came to tap. */}
      <PageNote title="What a handover needs">
        <NoteText>
          A photo, your location and the customer&apos;s signature — all three,
          every time (REQ-DLV-2). The form will not submit without them.
        </NoteText>
        <NoteText>
          Where money is due, the amount shown is what is{" "}
          <strong>still outstanding now</strong>, not what the bill was for. A
          collection may already have been taken in the field.
        </NoteText>
        <NoteText>
          A battery coming back from service has nothing to collect — nothing
          was sold.
        </NoteText>
      </PageNote>
    </Page>
  );
}

function Chevron() {
  return <IconChevronRight size={18} stroke={1.8} color="var(--mantine-color-gray-5)" />;
}
